import React, { useEffect, useState, useCallback } from "react";

// Assuming these imports are correct based on your original code
import image4 from "../../assets/image4.png";
import image7 from "../../assets/image7.png";
import image8 from "../../assets/image8.png";
import image9 from "../../assets/image9.png";
import image10 from "../../assets/image10.png";
import image11 from "../../assets/Bolero-pickup-featured-image.png";
import image12 from '../../assets/image-1-9feb.jpg';
import image13 from '../../assets/image-9-9feb.jpg';
import logo from "../../assets/logo.png";
import image14 from '../../assets/image14.webp';
import image15 from '../../assets/image15.jpg';
import image16 from '../../assets/image16.jpeg';

import { Offerings, OFFERINGS, Stakeholder, STAKEHOLDERS } from "./homeData";

// --- Data and Helper Components (Kept for completeness) ---

const ArrowRightSVG = () => (
  // ... (Your existing ArrowRightSVG component) ...
  <svg
    className="w-4 h-4 ml-2"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M14 5l7 7m0 0l-7 7m7-7H3"
    />
  </svg>
);

const NavItem = ({ children }: { children: React.ReactNode }) => (
  // ... (Your existing NavItem component) ...
  <a
    href="#"
    className="relative text-gray-700 hover:text-gray-900 transition duration-300 font-medium text-sm py-2"
  >
    {children}
    <span className="absolute bottom-0 left-0 w-0 h-[3px] bg-orange-500 rounded transition-all duration-300 hover:w-full"></span>
  </a>
);

const ConsultationButton = () => (
  // ... (Your existing ConsultationButton component) ...
  <>
    <button className="flex items-center bg-white  border border-orange-500 py-3 px-6 rounded-full hover:bg-orange-500 hover:text-orange-50 transition duration-300 shadow-md font-semibold text-sm">
      <span className="mr-2">Consultation</span>
    </button>
    <div className="text-white bg-orange-500 px-5 py-4 rounded-full">
      <ArrowRightSVG />
    </div>
  </>
);

// Stakeholder Card (Reusing your existing component)
const StakeholderCard = ({ stakeholder }: { stakeholder: Stakeholder }) => (
  // The card container now needs padding-top to account for the lifted avatar
  <div className="relative flex flex-col items-center w-full max-w-xs md:max-w-sm 
                    p-6 pt-16 bg-white rounded-xl shadow-2xl transition duration-500 
                    border border-gray-100/50">

    {/* 1. Avatar Container (Lifted Above the Card) */}
    {/* Use absolute positioning and -top to place it like the image */}
    <div className={`absolute -top-10 left-1/2 -translate-x-1/2 
                        p-2 bg-white rounded-full 
                        border-4 border-white shadow-xl
                        ring-4 ring-offset-2 ring-offset-white 
                        ring-orange-500/80`}>
      <img
        src={stakeholder.photoUrl}
        // Use a darker color or the accent color for the text background if photoUrl is a placeholder
        className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-full"
      />
    </div>

    {/* 2. Text Content */}
    <div className="text-center mt-2">
      <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-0">
        {stakeholder.name}
      </h3>
      {/* Designation/Title is smaller and uses the accent color */}
      <p className="text-sm text-gray-600 font-medium uppercase tracking-wider mb-4">
        {stakeholder.designation}
      </p>

      {/* 3. Main Content Area (Emulating the Review/Quote) */}
      {/* Placeholder for a quote or bio, using email/designation */}
      <div className="relative px-2 py-2 text-gray-700">
        {/* Large Quote Mark Icons using Teal for accent */}
        <svg className="absolute -top-4 -left-1 w-8 h-8 text-teal-100" fill="currentColor" viewBox="0 0 24 24"><path d="M11 6C8.791 6 7 7.791 7 10h4V6zm-4 7c0 2.761 2.239 5 5 5h1c2.761 0 5-2.239 5-5V9h-5v4H7z" /></svg>

        <p className="italic text-base leading-relaxed pt-2 text-gray-700">
          "Driving innovation through dedicated operations and clear strategic vision."
        </p>

        {/* Second Quote Mark, flipped */}
        <svg className="absolute -bottom-4 -right-1 w-8 h-8 text-teal-100 rotate-180" fill="currentColor" viewBox="0 0 24 24"><path d="M11 6C8.791 6 7 7.791 7 10h4V6zm-4 7c0 2.761 2.239 5 5 5h1c2.761 0 5-2.239 5-5V9h-5v4H7z" /></svg>
      </div>

      {/* Email link at the bottom (optional) */}
      <a
        href={`mailto:${stakeholder.email}`}
        className="text-xs text-orange-500 hover:text-orange-700 transition font-medium mt-4 block"
      >
        Connect: {stakeholder.email}
      </a>

    </div>
  </div>
);

const OfferingCard = ({ offering }: { offering: Offerings }) => (
  <div
    className="
      flex items-center gap-4 
      text-gray-800 
      transition-transform duration-300 hover:scale-105
    "
  >
    {/* ✅ Orange Circle Icon */}
    <div
      className="
    w-20 h-20 rounded-full 
    border-2 border-orange-500
    flex items-center justify-center 
    bg-orange-50
    shadow-md
    hover:shadow-orange-300
    transition
  "
    >
      {typeof offering.icon === "string" ? (
        <div
          className="w-10 h-10"
          dangerouslySetInnerHTML={{ __html: offering.icon }}
        />
      ) : (
        offering.icon
      )}
    </div>

    <h3 className="text-sm font-semibold text-gray-900">
      {offering.title}
    </h3>
  </div>
);

// --- New Navigation Button Component ---
const NavButton = ({ direction, onClick }: { direction: "prev" | "next", onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`absolute top-1/2 -translate-y-1/2 z-50 p-3 rounded-full bg-white/70 backdrop-blur-sm text-orange-500 shadow-xl transition-all hover:bg-white active:scale-95 ${direction === "prev" ? "left-4 sm:left-10" : "right-4 sm:right-10"
      }`}
    aria-label={`${direction === "prev" ? "Previous" : "Next"} Stakeholder`}
  >
    <svg
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d={direction === "prev" ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
      />
    </svg>
  </button>
);

const Footer = () => {

  const handleCallbackSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.target as HTMLFormElement);
    const firstName = formData.get("firstName") || "";
    const businessType = formData.get("businessType") || "";
    const contactNumber = formData.get("contactNumber") || "";

    const subject = encodeURIComponent("New Callback Request - XpressIndia");
    const body = encodeURIComponent(
      `New callback request from website:\n\n` +
      `Name: ${firstName}\n` +
      `Business Type / Inquiry: ${businessType}\n` +
      `Contact Number: ${contactNumber}\n`
    );

    window.location.href = `mailto:support@xpressind.com?subject=${subject}&body=${body}`;
  };

  const contactDetails = {
    address: "MAHAMMADPUR, KADAMPUKUR, RAJARHAT, NEWTOWN, NORTH 24 PARGANAS, WB - 700135.",
    phone1: "9051799383",
    phone2: "8697942454",
    email: "support@xpressind.com",
  };
  const businessTypes = [
    "First Mile Delivery",
    "Last Mile Delivery",
    "Middle Mile Delivery",
    "E-commerce Logistics",
    "Warehousing Solutions",
    "Reverse Logistics",
    "Sort Centers",
    "Store Delivery",
  ];

  return (
    <footer className="bg-gray-950 text-gray-300 pt-8 sm:pt-12 pb-4">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row justify-between gap-12">
          {/* ===== Left Section: Logo, Social, App Links, Copyright ===== */}
          <div className="lg:w-1/3 space-y-6">
            {/* Logo (Emulating the image's logo style) */}
            <div className="flex flex-col gap-2">
              <div className="text-xl font-extrabold tracking-wider text-white">
                <span className="text-orange-500">XPRESSINDIA</span> LOGISTICS PRIVATE LIMITED
              </div>
              {/* i need to show cin nunber here */}
              <div className="text-xs text-white">
                <span className="text-orange-500">CIN</span> - U52290WB2025PTC284709
              </div>
            </div>

            {/* Displaying Contact Information */}
            <div className="space-y-3 text-sm">
              <h3 className="text-lg font-bold text-white mb-2">Contact Us</h3>
              {/* Email */}
              <div className="flex items-center space-x-2">
                {/* Placeholder for Email Icon */}
                <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                <a href={`mailto:${contactDetails.email}`} className="hover:text-orange-500 transition duration-200">
                  {contactDetails.email}
                </a>
              </div>
              {/* Phone Numbers */}
              <div className="flex items-center space-x-2">
                {/* Placeholder for Phone Icon */}
                <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.5l1 4 2.5-1.5-1.5-2.5 1-4H19a2 2 0 012 2v14a2 2 0 01-2 2h-3.5l-1-4-2.5 1.5 1.5 2.5-1 4H5a2 2 0 01-2-2V5z"></path></svg>
                <p className='space-x-2'>
                  <a href={`tel:${contactDetails.phone1}`} className="hover:text-orange-500 transition duration-200">{contactDetails.phone1}</a> |
                  <a href={`tel:${contactDetails.phone2}`} className="hover:text-orange-500 transition duration-200">{contactDetails.phone2}</a>
                </p>
              </div>
              {/* Address */}
              <div className="flex space-x-2 pt-2">
                {/* Placeholder for Location Icon */}
                <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                <p className="text-gray-400">{contactDetails.address}</p>
              </div>
            </div>

            {/* Social Media Icons */}
            <div className="flex space-x-4">
              {['facebook', 'twitter', 'instagram'].map((platform) => (
                <a
                  key={platform}
                  href="#"
                  aria-label={platform}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 text-white hover:bg-orange-500 transition duration-300"
                >
                  {/* Simple SVG placeholders for social icons */}
                  {platform === 'facebook' && (
                    <svg fill="#ea580c" className="w-4 h-4" height="200px" width="200px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="-337 273 123.5 256" xmlSpace="preserve"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M-260.9,327.8c0-10.3,9.2-14,19.5-14c10.3,0,21.3,3.2,21.3,3.2l6.6-39.2c0,0-14-4.8-47.4-4.8c-20.5,0-32.4,7.8-41.1,19.3 c-8.2,10.9-8.5,28.4-8.5,39.7v25.7H-337V396h26.5v133h49.6V396h39.3l2.9-38.3h-42.2V327.8z"></path> </g></svg>
                  )}
                  {platform === 'twitter' && (
                    <svg fill="#ea580c" className="w-4 h-4" height="200px" width="200px" version="1.1" id="Layer_1" viewBox="0 0 1920 1920" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M1920 311.856c-70.701 33.769-146.598 56.47-226.221 66.86 81.317-52.517 143.774-135.529 173.252-234.691-76.236 48.678-160.716 84.028-250.391 103.002-71.718-82.56-174.268-134.06-287.435-134.06-217.75 0-394.165 189.966-394.165 424.206 0 33.318 3.614 65.619 10.165 96.678C617.9 616.119 327.304 447.385 133.045 190.67c-33.77 62.57-53.309 135.53-53.309 213.233 0 147.162 91.031 276.818 196.744 353.054-64.602-2.26-157.101-21.46-157.101-53.309v5.648c0 205.327 114.41 376.658 294.55 415.849-32.978 9.487-78.38 14.795-114.409 14.795-25.412 0-55.454-2.71-79.624-7.793 50.26 168.509 193.13 291.163 365.478 294.777-134.852 113.506-306.07 181.383-490.616 181.383-31.85 0-64.038-2.033-94.758-5.873 174.494 120.17 381.176 190.532 603.67 190.532 724.97 0 1121.055-646.136 1121.055-1206.55 0-18.41-.452-36.932-1.356-55.116 77.026-59.746 143.887-134.4 196.631-219.444" fill-rule="evenodd"></path> </g></svg>
                  )}
                  {platform === 'instagram' && (
                    <svg viewBox="0 0 20 20" width="20" height="20" className="w-4 h-4" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" fill="#ea580c"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <title>instagram [#167]</title> <desc>Created with Sketch.</desc> <defs> </defs> <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="Dribbble-Light-Preview" transform="translate(-340.000000, -7439.000000)" fill="#ea580c"> <g id="icons" transform="translate(56.000000, 160.000000)"> <path d="M289.869652,7279.12273 C288.241769,7279.19618 286.830805,7279.5942 285.691486,7280.72871 C284.548187,7281.86918 284.155147,7283.28558 284.081514,7284.89653 C284.035742,7285.90201 283.768077,7293.49818 284.544207,7295.49028 C285.067597,7296.83422 286.098457,7297.86749 287.454694,7298.39256 C288.087538,7298.63872 288.809936,7298.80547 289.869652,7298.85411 C298.730467,7299.25511 302.015089,7299.03674 303.400182,7295.49028 C303.645956,7294.859 303.815113,7294.1374 303.86188,7293.08031 C304.26686,7284.19677 303.796207,7282.27117 302.251908,7280.72871 C301.027016,7279.50685 299.5862,7278.67508 289.869652,7279.12273 M289.951245,7297.06748 C288.981083,7297.0238 288.454707,7296.86201 288.103459,7296.72603 C287.219865,7296.3826 286.556174,7295.72155 286.214876,7294.84312 C285.623823,7293.32944 285.819846,7286.14023 285.872583,7284.97693 C285.924325,7283.83745 286.155174,7282.79624 286.959165,7281.99226 C287.954203,7280.99968 289.239792,7280.51332 297.993144,7280.90837 C299.135448,7280.95998 300.179243,7281.19026 300.985224,7281.99226 C301.980262,7282.98483 302.473801,7284.28014 302.071806,7292.99991 C302.028024,7293.96767 301.865833,7294.49274 301.729513,7294.84312 C300.829003,7297.15085 298.757333,7297.47145 289.951245,7297.06748 M298.089663,7283.68956 C298.089663,7284.34665 298.623998,7284.88065 299.283709,7284.88065 C299.943419,7284.88065 300.47875,7284.34665 300.47875,7283.68956 C300.47875,7283.03248 299.943419,7282.49847 299.283709,7282.49847 C298.623998,7282.49847 298.089663,7283.03248 298.089663,7283.68956 M288.862673,7288.98792 C288.862673,7291.80286 291.150266,7294.08479 293.972194,7294.08479 C296.794123,7294.08479 299.081716,7291.80286 299.081716,7288.98792 C299.081716,7286.17298 296.794123,7283.89205 293.972194,7283.89205 C291.150266,7283.89205 288.862673,7286.17298 288.862673,7288.98792 M290.655732,7288.98792 C290.655732,7287.16159 292.140329,7285.67967 293.972194,7285.67967 C295.80406,7285.67967 297.288657,7287.16159 297.288657,7288.98792 C297.288657,7290.81525 295.80406,7292.29716 293.972194,7292.29716 C292.140329,7292.29716 290.655732,7290.81525 290.655732,7288.98792" id="instagram-[#167]"> </path> </g> </g> </g> </g></svg>
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* ===== Middle Section: Quick Links & Company Links (Flex-row on desktop) ===== */}
          <div className="lg:w-1/3 flex flex-col space-y-8">

            {/* Top Links Section */}
            <div className="flex justify-between space-x-8">

              {/* Quick Links */}
              <div className="min-w-[100px]">
                <h3 className="text-lg font-bold text-white mb-4">Quick Links</h3>
                <ul className="space-y-2 text-sm">
                  {['Home', 'About', 'Tracking', 'Solutions', 'Stackeholders'].map((link) => (
                    <li key={link}>
                      <a href="#" className="hover:text-orange-500 transition duration-200">
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company Links */}
              <div className="min-w-[100px]">
                <h3 className="text-lg font-bold text-white mb-4">Company</h3>
                <ul className="space-y-2 text-sm">
                  {['About Us', 'Careers', 'FAQs', 'Contact Us', 'Privacy'].map((link) => (
                    <li key={link}>
                      <a href="#" className="hover:text-orange-500 transition duration-200">
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* ✅ Bottom Consultation + Reviews Section */}
            <div className="pt-6 border-t border-white/10 flex justify-between">

              <div className="flex space-x-2 items-center text-sm text-gray-500 mt-3">

                {/* Avatars */}
                <div className="flex -space-x-3">
                  {[1, 2].map((_, i) => (
                    <div
                      key={i}
                      className="w-10 h-10 bg-gray-300 rounded-full border-2 border-white flex items-center justify-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-5 h-5 text-gray-600"
                      >
                        <path
                          fillRule="evenodd"
                          d="M12 12a5 5 0 100-10 5 5 0 000 10zm-7 9a7 7 0 0114 0H5z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  ))}
                </div>

                {/* Review Count */}
                <span className="mt-1 text-sm leading-tight">
                  <span className="font-extrabold text-white">6.5K</span>
                  <span className="font-extrabold text-orange-500">+</span>
                  <br />
                  <span className="font-semibold text-gray-400">of reviews</span>
                </span>

              </div>
              <div className="flex items-center space-x-1">
                <ConsultationButton />
              </div>
            </div>

          </div>
          {/* ===== Right Section: Request a Call Back Form ===== */}
          <div className="lg:w-1/3">
            {/* Outer dark blue box (optional, but mimics image) */}
            <div className="bg-gray-800 p-2 rounded-xl shadow-2xl">
              {/* Call Back Header */}
              <div className="flex justify-between items-center px-4 py-2">
                <h3 className="text-lg font-bold text-white">Request a Call Back</h3>
                <button aria-label="Submit" className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition">
                  <ArrowRightSVG />
                </button>
              </div>

              {/* Inner light box for form */}
              <form
                className="p-4 bg-white/5 rounded-xl space-y-4"
                onSubmit={handleCallbackSubmit}
              >
                <div className="grid grid-cols-2 gap-3">
                  {/* First Name */}
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    required
                  />

                  {/* Business Planning */}
                  <select
                    name="businessType"
                    className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-gray-700 text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm appearance-none"
                    defaultValue=""
                    required
                  >
                    <option value="" disabled>
                      Business Planning
                    </option>
                    {businessTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {/* Contact Number */}
                  <input
                    type="tel"
                    name="contactNumber"
                    placeholder="Contact Number"
                    className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    required
                  />

                  {/* Submit Button (Orange) */}
                  <button
                    type="submit"
                    className="w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition font-bold shadow-md text-sm"
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <hr className="border-gray-800 mt-8 mb-4" />

        {/* Bottom Line */}
        <div className="text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} LOGISIDO. All Rights Reserved.
        </div>
      </div>
    </footer>
  );
};

// --- Home Component Start ---
export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState("next"); // Kept direction state but it's less critical now

  const totalCards = STAKEHOLDERS.length;

  // ✅ NEW getPosition logic adapted from your sample
  const getPosition = useCallback((index: number): number => {
    let diff = index - activeIndex;

    // Wrap around logic
    if (diff > totalCards / 2) {
      diff -= totalCards;
    } else if (diff < -totalCards / 2) {
      diff += totalCards;
    }

    return diff;
  }, [activeIndex, totalCards]);


  const goToNext = useCallback(() => {
    setDirection("next");
    setActiveIndex((prev) => (prev + 1) % totalCards);
  }, [totalCards]);

  const goToPrev = useCallback(() => {
    setDirection("prev");
    setActiveIndex((prev) => (prev - 1 + totalCards) % totalCards);
  }, [totalCards]);


  // ✅ Auto slide (now uses the new goToNext)
  useEffect(() => {
    const timer = setInterval(() => {
      goToNext();
    }, 3500);

    return () => clearInterval(timer);
  }, [goToNext]); // Depend on goToNext


  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 font-sans">

      {/* ... (Your existing NAVBAR and HERO SECTION code) ... */}

      {/* ================= NAVBAR ================= */}
      <header className="**w-full** px-4 sm:px-6 lg:px-8 py-4 sticky top-0 z-50 bg-white shadow-md h-20">
        {/* The container is moved inside to manage content width, while the header itself takes up the full screen width. */}
        <div className="container mx-auto h-full">
          <nav className="flex justify-between items-center h-full">

            {/* Logo Fix: Changed 'top-1' to 'top-0' or a slightly negative value to better center a large logo over a small navbar. */}
            {/* Position is now relative to the 'header' which is full width. */}
            <div className="left-4 md:left-8 flex-shrink-0 z-50">
              <a href="#home">
                <img
                  src={logo}
                  alt="Company Logo"
                  className="h-40 w-auto " // Logo height set to h-40
                />
              </a>
            </div>

            {/* Desktop Navigation Links */}
            {/* Added ml-64 to push the links past the space occupied by the large logo */}
            <div className="hidden lg:flex items-center space-x-8 ml-64">
              <NavItem>
                <a href="#home">Home</a>
              </NavItem>

              <NavItem>
                <a href="#track">Track Order</a>
              </NavItem>

              <NavItem>
                <a href="#solution">Solution</a>
              </NavItem>

              <NavItem>
                <a href="#stakeholders">Stakeholders</a>
              </NavItem>

              <NavItem>
                <a href="#about">About Us</a>
              </NavItem>

              <button className="bg-orange-500 text-white py-3 px-8 rounded-lg hover:bg-orange-600 transition duration-300 font-bold shadow-lg text-sm">
                Connect Now
              </button>
            </div>

            {/* Mobile Menu Button (Make sure this button is positioned correctly since it's now inside the 'container mx-auto' div) */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-800 focus:outline-none"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"}
                  />
                </svg>
              </button>
            </div>
          </nav>
        </div>

        {/* Mobile Menu Dropdown - Needs position adjustment if header is now full width */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 bg-white shadow-lg rounded-lg py-4 absolute w-full left-0">
            <div className="flex flex-col items-center space-y-3">
              <NavItem><a href="#home">Home</a></NavItem>
              <NavItem><a href="#track">Track Order</a></NavItem>
              <NavItem><a href="#solution">Solution</a></NavItem>
              <NavItem><a href="#stakeholders">Stakeholders</a></NavItem>
              <NavItem><a href="#about">About Us</a></NavItem>
              <button className="bg-orange-500 text-white w-2/3 py-3 rounded-lg hover:bg-orange-600 font-bold shadow-md mt-3 text-sm">
                Connect Now
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ... (Existing HERO SECTION code) ... */}
      <section id="home" className="container mx-auto px-4 sm:px-6 lg:px-8 mt-1">
        <div className="flex flex-col-reverse lg:flex-row items-center gap-12">
          <div className="lg:w-3/5 w-full">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-4 tracking-tighter">
              FLEXIBLE <span className="text-orange-500">LOGISTICS </span>
              & DELIVERY SERVICES
            </h1>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-6">
              Deliver Packages in any Way
            </h2>
            <p className="text-gray-600 mb-10 max-w-xl text-base">
              Our logistics services provide end-to-end solutions for all your
              shipping needs. Optimize your supply chain, leverage technology,
              trace shipments, manage inventory, and ensure real-time visibility.
            </p>
          </div>
          <div className="lg:w-2/5 w-full flex justify-center lg:justify-end">
            <div className="w-full max-w-md h-[260px] sm:h-[320px] md:h-[360px] lg:h-[420px]">
              <img
                src={image15}
                alt="Logistics Hero"
                className="w-full h-full object-contain"
              />

            </div>
          </div>
        </div>
      </section>

      {/* ... (Existing NEXT SECTION PLACEHOLDER code) ... */}
      <section id="track" className="container mx-auto  mt-10">
        <div className="flex flex-col-reverse lg:flex-row justify-between gap-12">
          <div className="lg:w-1/2 w-full flex  lg:justify-start">
            <div className="w-full max-w-lg">
              <img
                src={image14}
                alt="Logistics Container"
                className="w-full object-contain"
              />
            </div>
          </div>
          <div className="lg:w-1/2 w-full">
            <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-4">
              Deliver Your <span className="text-orange-500">Logistic </span>
              Safely & Quickly
            </h2>
            <p className="text-gray-600 max-w-lg mb-6">
              From start to finish, our seamless logistics service ensures a
              successful delivery experience every time. Get started now and see
              the difference for yourself.
            </p>
            {/* <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 max-w-md">
              <input
                type="text"
                placeholder="Enter your tracking ID"
                className="w-full sm:flex-1 px-4 py-3 rounded-full border border-gray-300 
                    focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                    text-sm shadow-sm"
              />
              <button className="bg-orange-500 text-white px-6 py-3 rounded-full 
                    hover:bg-orange-600 transition font-semibold shadow-md text-sm">
                Track Order
              </button>
            </div> */}
            <div className="flex justify-end w-full items-center">
              <div className="
                    w-42 h-32
                    sm:w-48 sm:h-40
                    md:w-52 md:h-48
                    lg:w-56 lg:h-56
                    ">
                <img
                  src={image7}
                  alt="Feature"
                  className="w-full h-full object-contain opacity-30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ... (Existing What we provided section code) ... */}
      <section id="solution" className="container mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-extrabold">
            WHAT WE <span className="text-orange-500">PROVIDE</span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto text-sm">
            We provide complete logistics solutions with speed, safety, and reliability.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
          {/* Card 1 */}
          <div className="relative overflow-hidden rounded-r-full">
            <div className="absolute top-0 right-0 h-full w-1/2 shadow-2xl rounded-r-3xl pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-l from-orange-200 via-orange-100 to-orange-50"></div>
            <div className="relative z-10 p-8 text-center">
              <div className="w-14 h-14  shadow-md rounded-full flex items-center justify-center mx-auto mb-4 text-orange-500 text-xl">
                <svg fill="#ea580c" height="200px" width="200px" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 307.984 307.984" xmlSpace="preserve"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M302.409,5.883c-1.652-0.815-3.46-1.132-5.228-0.989c0.021-0.014,0.04-0.029,0.061-0.043H103.917 c-2.13,0-4.204,0.68-5.92,1.941L4.254,75.66c0.008-0.003,0.016-0.004,0.023-0.006C1.693,77.462,0,80.455,0,83.847v209.287 c0,5.523,4.477,10,10,10h197.99c2.522,0,4.82-0.941,6.579-2.481c0.096-0.083,0.195-0.161,0.288-0.249l89.994-85 c2-1.889,3.133-4.519,3.133-7.27V14.851C307.984,11.044,305.823,7.567,302.409,5.883z M107.195,24.851h64.577l-59.68,48.997h-71.59 L107.195,24.851z M147.917,142.134h-72c-8.271,0-15-6.729-15-15s6.729-15,15-15h72c8.271,0,15,6.729,15,15 S156.188,142.134,147.917,142.134z M204.598,73.847h-62.536l59.681-48.997h66.763L204.598,73.847z"></path> </g></svg>
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">
                First Mile Delivery ( Full Truck Load )
              </h3>
              <p className="text-sm text-gray-700 max-w-xs mx-auto">
                We provide first-mile delivery services to ensure prompt and efficient delivery to your doorstep.
              </p>
            </div>
          </div>
          {/* Card 2 (Highlighted) */}
          <div className="relative rounded-3xl p-10 text-center scale-105">
            <div className="relative z-10 text-white">
              <div className="w-16 h-16 text-orange-500 rounded-full flex items-center justify-center mx-auto mb-5 text-xl">
                <svg fill="#ea580c" height="200px" width="200px" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 422.518 422.518" xmlSpace="preserve"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M422.512,215.424c0-0.079-0.004-0.158-0.005-0.237c-0.116-5.295-4.368-9.514-9.727-9.514h-2.554l-39.443-76.258 c-1.664-3.22-4.983-5.225-8.647-5.226l-67.34-0.014l2.569-20.364c0.733-8.138-1.783-15.822-7.086-21.638 c-5.293-5.804-12.683-9.001-20.81-9.001h-209c-5.255,0-9.719,4.066-10.22,9.308l-2.095,16.778h119.078 c7.732,0,13.836,6.268,13.634,14c-0.203,7.732-6.635,14-14.367,14H126.78c0.007,0.02,0.014,0.04,0.021,0.059H10.163 c-5.468,0-10.017,4.432-10.16,9.9c-0.143,5.468,4.173,9.9,9.641,9.9H164.06c7.168,1.104,12.523,7.303,12.326,14.808 c-0.216,8.242-7.039,14.925-15.267,14.994H54.661c-5.523,0-10.117,4.477-10.262,10c-0.145,5.523,4.215,10,9.738,10h105.204 c7.273,1.013,12.735,7.262,12.537,14.84c-0.217,8.284-7.109,15-15.393,15H35.792v0.011H25.651c-5.523,0-10.117,4.477-10.262,10 c-0.145,5.523,4.214,10,9.738,10h8.752l-3.423,35.818c-0.734,8.137,1.782,15.821,7.086,21.637c5.292,5.805,12.683,9.001,20.81,9.001 h7.55C69.5,333.8,87.3,349.345,109.073,349.345c21.773,0,40.387-15.545,45.06-36.118h94.219c7.618,0,14.83-2.913,20.486-7.682 c5.172,4.964,12.028,7.682,19.514,7.682h1.55c3.597,20.573,21.397,36.118,43.171,36.118c21.773,0,40.387-15.545,45.06-36.118h6.219 c16.201,0,30.569-13.171,32.029-29.36l6.094-67.506c0.008-0.091,0.004-0.181,0.01-0.273c0.01-0.139,0.029-0.275,0.033-0.415 C422.52,215.589,422.512,215.508,422.512,215.424z M109.597,329.345c-13.785,0-24.707-11.214-24.346-24.999 c0.361-13.786,11.87-25.001,25.655-25.001c13.785,0,24.706,11.215,24.345,25.001C134.89,318.131,123.382,329.345,109.597,329.345z M333.597,329.345c-13.785,0-24.706-11.214-24.346-24.999c0.361-13.786,11.87-25.001,25.655-25.001 c13.785,0,24.707,11.215,24.345,25.001C358.89,318.131,347.382,329.345,333.597,329.345z M396.457,282.588 c-0.52,5.767-5.823,10.639-11.58,10.639h-6.727c-4.454-19.453-21.744-33.882-42.721-33.882c-20.977,0-39.022,14.429-44.494,33.882 h-2.059c-2.542,0-4.81-0.953-6.389-2.685c-1.589-1.742-2.337-4.113-2.106-6.676l12.609-139.691l28.959,0.006l-4.59,50.852 c-0.735,8.137,1.78,15.821,7.083,21.637c5.292,5.806,12.685,9.004,20.813,9.004h56.338L396.457,282.588z"></path> </g></svg>
              </div>
              <h3 className="font-bold text-black text-lg mb-3">Middle Mile Delivery ( Part Load )</h3>
              <p className="text-gray-700 text-sm">
                Our middle-mile delivery services ensure efficient transportation between distribution centers.
              </p>
            </div>
          </div>
          {/* Card 3 */}
          <div className="relative overflow-hidden rounded-l-full">
            <div className="absolute top-0 left-0 h-full w-1/2 shadow-2xl rounded-l-3xl pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-orange-200 via-orange-100 to-orange-50"></div>
            <div className="relative z-10 p-8 text-center">
              <div className="w-14 h-14  flex items-center justify-center mx-auto mb-4 text-orange-500 text-xl">
                <svg fill="#ea580c" height="200px" width="200px" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 372.372 372.372" xmlSpace="preserve"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M368.712,219.925c-5.042-8.951-14.563-14.511-24.848-14.511c-4.858,0-9.682,1.27-13.948,3.672l-83.024,46.756 c-1.084,0.61-1.866,1.642-2.163,2.85c-1.448,5.911-4.857,14.164-12.865,19.911c-8.864,6.361-20.855,7.686-35.466,3.939 c-0.088-0.022-0.175-0.047-0.252-0.071L148.252,267.6c-2.896-0.899-4.52-3.987-3.621-6.882c0.72-2.316,2.83-3.872,5.251-3.872 c0.55,0,1.101,0.084,1.634,0.249l47.645,14.794c0.076,0.023,0.154,0.045,0.232,0.065c11.236,2.836,20.011,2.047,26.056-2.288 c7.637-5.48,8.982-15.113,9.141-16.528c0.006-0.045,0.011-0.09,0.014-0.136c0.003-0.023,0.004-0.036,0.005-0.039 c0.001-0.015,0.002-0.03,0.003-0.044c0.001-0.01,0.001-0.019,0.002-0.029c0.909-11.878-6.756-22.846-18.24-26.089l-0.211-0.064 c-0.35-0.114-35.596-11.626-58.053-18.034c-2.495-0.711-9.37-2.366-19.313-2.366c-13.906,0-34.651,3.295-54.549,19.025 L1.67,292.159c-1.889,1.527-2.224,4.278-0.758,6.215l44.712,59.06c0.725,0.956,1.801,1.584,2.99,1.744 c0.199,0.027,0.398,0.04,0.598,0.04c0.987,0,1.954-0.325,2.745-0.935l57.592-44.345c1.294-0.995,3.029-1.37,4.619-0.995 l93.02,21.982c6.898,1.63,14.353,0.578,20.523-2.9l130.16-73.304C371.555,251.012,376.418,233.61,368.712,219.925z"></path> <path d="M316.981,13.155h-170c-5.522,0-10,4.477-10,10v45.504c0,5.523,4.478,10,10,10h3.735v96.623c0,5.523,4.477,10,10,10h142.526 c5.523,0,10-4.477,10-10V78.658h3.738c5.522,0,10-4.477,10-10V23.155C326.981,17.632,322.503,13.155,316.981,13.155z M253.016,102.417h-42.072c-4.411,0-8-3.589-8-8c0-4.411,3.589-8,8-8h42.072c4.411,0,8,3.589,8,8 C261.016,98.828,257.427,102.417,253.016,102.417z M306.981,58.658h-3.738H160.716h-3.735V33.155h150V58.658z"></path> </g> </g></svg>
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">
                Last Mile Delivery ( E-Commerce )
              </h3>
              <p className="text-sm text-gray-700 max-w-sm mx-auto">
                Our last-mile delivery services ensure your packages reach their final destination quickly and safely.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our offirings section i need here */}

      <section id="our-offerings" className="container mx-auto px-3 sm:px-4 py-7 overflow-hidden">
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold">
            OUR <span className="text-orange-500">OFFERINGS</span>
          </h2>
          <p className="text-gray-500 mt-2 sm:mt-3 max-w-xl mx-auto text-xs sm:text-sm">
            We offer a wide range of services to meet your logistic needs.
          </p>
        </div>

        <div className="
  grid 
  grid-cols-1 
  sm:grid-cols-2 
  md:grid-cols-3 
  lg:grid-cols-4
  gap-10 
  place-items-center
">
          {OFFERINGS.map((offering, index) => (
            <OfferingCard key={index} offering={offering} />
          ))}
        </div>
      </section>

      {/* ================= REVISED STAKEHOLDERS SECTION (3D Carousel) ================= */}
      <section id="stakeholders" className="container mx-auto px-3 sm:px-4 py-7 overflow-hidden">

        {/* ===== Title ===== */}
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold">
            OUR <span className="text-orange-500">STAKEHOLDERS</span>
          </h2>
          <p className="text-gray-500 mt-2 sm:mt-3 max-w-xl mx-auto text-xs sm:text-sm">
            Meet the visionaries driving our logistics excellence.
          </p>
        </div>

        {/* normal card view i need here  */}

        <div className="
  w-full  items-center
  grid grid-cols-1 sm:grid-cols-2
  gap-y-16 
  place-items-center
  pt-16
">
          {STAKEHOLDERS.map((stakeholder, index) => (
            <div key={index} className="flex justify-center">
              <StakeholderCard stakeholder={stakeholder} />
            </div>
          ))}

        </div>
      </section>

      {/* About as section with same image as hero section */}

      <section
        id="about"
        className="bg-white w-full mx-auto px-4 sm:px-6 md:px-8 lg:pb-2 flex flex-col lg:flex-row items-center gap-8"
      >
        {/* ===== LEFT CONTENT ===== */}
        <div className="text-center lg:text-left w-full lg:w-1/2 mt-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight">
            ABOUT <span className="text-orange-500">US</span> – XpressIndia{" "}
            <span className="text-orange-500">Logistics</span> Private Limited
          </h2>

          <p className="text-gray-500 mt-3 max-w-2xl mx-auto lg:mx-0 text-sm sm:text-base">
            XpressIndia Logistics Private Limited is a modern, smart, and reliable
            logistics solutions provider. Our mission is to deliver fast, secure,
            and technology-driven logistics services across West Bengal and the
            entire Eastern Zone.
          </p>

          <p className="text-gray-500 mt-3 max-w-2xl mx-auto lg:mx-0 text-sm sm:text-base">
            We believe that delivering goods on time and at the right destination
            is not just a responsibility—it is a way to build trust, strengthen
            relationships, and support business growth.
          </p>

          {/* Decorative Background Image */}
          <div className="flex justify-center lg:justify-start mt-6">
            <div
              className="
          w-32 h-24
          sm:w-40 sm:h-32
          md:w-48 md:h-40
          lg:w-56 lg:h-48
        "
            >
              <img
                src={image10}
                alt="Feature"
                className="w-full h-full object-contain opacity-30"
              />
            </div>
          </div>
        </div>

        {/* ===== RIGHT IMAGE ===== */}
        <div className="w-full lg:w-1/2 flex justify-center lg:justify-end">
          <div className="w-full max-w-xs sm:max-w-sm md:max-w-md h-[240px] sm:h-[320px] md:h-[380px] lg:h-[450px]">
            <img
              src={image16}
              alt="Logistics Hero"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* show map section  */}
      {/* <section id="global" className="w-full mx-auto px-4 sm:px-6 md:px-8 pb-10 lg:pb-16 flex flex-col items-center">

        <div className="text-center w-full mt-8 mb-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold">
            OUR <span className="text-orange-500">GLOBAL REACH</span>
          </h2>

          <p className="text-gray-500 mt-2 sm:mt-3 max-w-2xl mx-auto text-sm sm:text-base px-4">
            We are proud to serve clients worldwide, ensuring timely and efficient
            deliveries across the globe.
          </p>
        </div>

        <div className="w-full flex justify-center lg:justify-end mt-6">
          <div className="w-full h-[250px] sm:h-[350px] md:h-[400px] lg:h-[450px] shadow-lg rounded-lg overflow-hidden">

            <iframe
              className="w-full h-full border-0"
              src="https://www.google.com/maps?q=MAHAMMADPUR,+KADAMPUKUR,+RAJARHAT,+NEWTOWN,+NORTH+24+PARGANAS,+WB+700135&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Global Reach Map"
              allowFullScreen
            ></iframe>
          </div>
        </div>

      </section> */}

      <Footer />
    </div >
  );
}